import { Form, Formik, useFormikContext } from "formik";
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ReactComponent as ClipboardIcon } from "assets/images/common/clipboard.svg";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import SearchableSelect from "components/SearchableSelect";
import Select from "components/Select";
import TextInput from "components/TextInput";

import useDebounce from "hooks/useDebounce";
import useValidation from "hooks/useValidation";

import { CompaniesApi, GatewaysApi, PortfoliosApi } from "services/http";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import { BaseOption } from "entities/common";
import {
  EUI_IMAGE_ALLOWED_TYPES,
  EUI_IMAGE_MAX_SIZE,
  FREQUENCY_BANDS,
  GATEWAY_EUI_MAX_LENGTH,
  GATEWAY_MODELS,
} from "entities/gateways";

import { RootStore } from "store";
import { useAppSelector } from "store/hooks";

import { validationSchema } from "../validationSchema";

import styles from "../styles.module.scss";

export type GatewayFormValues = {
  name: string;
  companyId: string;
  companyLabel?: string;
  portfolioId: string;
  portfolioLabel?: string;
  gatewayModel: string;
  frequencyBand: string;
  gatewayEui: string;
};

type PropTypes = {
  onClose?: () => void;
  onSubmit?: (values: GatewayFormValues) => void;
  mode?: "create" | "edit";
  description?: string;
  initialData?: GatewayFormValues;
  isLoading?: boolean;
};

type FormFieldsProps = {
  onCancel: () => void;
  mode: "create" | "edit";
  isLoading?: boolean;
};

const SELECT_PAGE_SIZE = 50;

const FREQUENCY_OPTIONS: BaseOption[] = FREQUENCY_BANDS.map((band) => ({
  value: band,
  label: band,
}));

const GATEWAY_MODEL_OPTIONS: BaseOption[] = GATEWAY_MODELS.map((model) => ({
  value: model,
  label: model,
}));

const UPLOAD_TOOLTIP =
  "Upload a close-up photo of the EUI label. Make sure the label is clearly visible and in focus";
const LOCKED_FIELD_TOOLTIP = "Cannot be changed after registration. To replace — delete and re-add";

const FormFields: FC<FormFieldsProps> = ({ onCancel, mode, isLoading }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tooltip, setTooltip] = useState<{
    content: string;
    left: number;
    top: number;
    placement: "right" | "below";
  } | null>(null);
  const { values, errors, touched, setFieldValue, setFieldTouched, handleChange, handleBlur, handleSubmit, setErrors } =
    useFormikContext<GatewayFormValues>();

  const isCreate = mode === "create";
  const gatewayErrors = useAppSelector((state: RootStore) => state.gateways.errors);

  const [euiRecognitionError, setEuiRecognitionError] = useState<string | undefined>(undefined);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const debouncedEui = useDebounce(values.gatewayEui, 300);
  const lastDetectedFor = useRef<string>("");

  useValidation(formRef, errors);

  useEffect(() => {
    if (!isCreate) return;
    const eui = (debouncedEui || "").trim();
    if (!eui) return;
    if (lastDetectedFor.current === eui) return;
    lastDetectedFor.current = eui;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await GatewaysApi.detectModel(eui);
        if (cancelled) return;
        if (data?.gateway_model && data.gateway_model !== values.gatewayModel) {
          setFieldValue("gatewayModel", data.gateway_model);
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedEui, isCreate, setFieldValue, values.gatewayModel]);

  // Map server-side validation errors onto the form.
  useEffect(() => {
    if (!gatewayErrors?.detail) return;
    const detail = gatewayErrors.detail;
    setErrors({
      ...(detail.gateway_name && { name: detail.gateway_name }),
      ...(detail.company_id && { companyId: detail.company_id }),
      ...(detail.portfolio_id && { portfolioId: detail.portfolio_id }),
      ...(detail.gateway_model && { gatewayModel: detail.gateway_model }),
      ...(detail.frequency_band && { frequencyBand: detail.frequency_band }),
      ...(detail.gateway_eui && { gatewayEui: detail.gateway_eui }),
    });
  }, [gatewayErrors, setErrors]);

  const searchCompanies = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
        }),
      );
      const options: BaseOption[] = (data?.items ?? []).map((c) => ({
        value: String(c.id),
        label: c.name,
      }));
      const hasMore = (data?.total ?? 0) > page * SELECT_PAGE_SIZE;
      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const handleCompanyChange = useCallback(
    (value: string, label: string) => {
      setFieldValue("companyId", value);
      setFieldValue("companyLabel", label);
      // FR 6.2.1.1, field 18, rule 1: if Company changes while Portfolio is set,
      // clear Portfolio.
      if (values.portfolioId) {
        setFieldValue("portfolioId", "");
        setFieldValue("portfolioLabel", "");
      }
    },
    [setFieldValue, values.portfolioId],
  );

  const searchPortfolios = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await PortfoliosApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            // If Company is set, scope portfolios to it; otherwise list all.
            ...(values.companyId
              ? { [QUERY_PARAMS.COMPANY_ID]: Number(values.companyId) }
              : {}),
          }),
        );
        const options: BaseOption[] = (data?.items ?? []).map((p) => ({
          value: String(p.id),
          label: p.name,
          companyId: p.company_id,
          companyName: p.company_name,
        }));
        const hasMore = (data?.total ?? 0) > page * SELECT_PAGE_SIZE;
        return { options, hasMore };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [values.companyId],
  );

  const handlePortfolioChange = useCallback(
    (value: string, label: string, option?: BaseOption) => {
      setFieldValue("portfolioId", value);
      setFieldValue("portfolioLabel", label);
      // FR 6.2.1.1, field 18, rule 1: if Company is empty and user picks a
      // Portfolio, auto-fill Company from that portfolio.
      if (!values.companyId && option?.companyId) {
        setFieldValue("companyId", String(option.companyId));
        setFieldValue("companyLabel", option.companyName ?? "");
      }
    },
    [setFieldValue, values.companyId],
  );

  const handleSelectChange = useCallback(
    (field: keyof GatewayFormValues) => (value: string) => {
      setFieldValue(field, value);
      setFieldTouched(field, true, false);
    },
    [setFieldValue, setFieldTouched],
  );

  const handleUploadClick = useCallback(() => {
    if (isLoading || isRecognizing) return;
    fileInputRef.current?.click();
  }, [isLoading, isRecognizing]);

  const showTooltip = useCallback((element: HTMLElement, content: string) => {
    const rect = element.getBoundingClientRect();
    const dialog = document.querySelector("dialog[open]");
    if (!dialog) return;
    const dialogRect = dialog.getBoundingClientRect();

    const GAP = 12;
    const TOOLTIP_MAX_WIDTH = 240;
    const VIEWPORT_PADDING = 8;
    const fitsRight = rect.right + GAP + TOOLTIP_MAX_WIDTH <= window.innerWidth - VIEWPORT_PADDING;

    setTooltip(
      fitsRight
        ? {
            content,
            left: rect.right + GAP - dialogRect.left,
            top: rect.top + rect.height / 2 - dialogRect.top,
            placement: "right",
          }
        : {
            content,
            left: rect.right - dialogRect.left,
            top: rect.bottom + GAP - dialogRect.top,
            placement: "below",
          },
    );
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltip(null);
  }, []);

  const handleFileSelected = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input value so the same file can be re-selected after a failure.
      e.target.value = "";
      if (!file) return;

      if (!(EUI_IMAGE_ALLOWED_TYPES as readonly string[]).includes(file.type)) {
        setEuiRecognitionError("Allowed formats: JPEG, PNG, WEBP.");
        return;
      }
      if (file.size > EUI_IMAGE_MAX_SIZE) {
        setEuiRecognitionError("File size must not exceed 5 MB.");
        return;
      }

      setEuiRecognitionError(undefined);
      setIsRecognizing(true);
      try {
        const { data } = await GatewaysApi.recognizeEui(file);
        if (data?.gateway_eui) {
          setFieldValue("gatewayEui", data.gateway_eui.slice(0, GATEWAY_EUI_MAX_LENGTH));
          setFieldTouched("gatewayEui", true, false);
        } else {
          setEuiRecognitionError("Unable to recognize Gateway EUI. Please try again.");
        }
      } catch {
        setEuiRecognitionError("Unable to recognize Gateway EUI. Please try again.");
      } finally {
        setIsRecognizing(false);
      }
    },
    [setFieldValue, setFieldTouched],
  );

  useEffect(() => {
    if (euiRecognitionError) setEuiRecognitionError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.gatewayEui]);

  return (
    <div ref={formRef} className={styles.content}>
      <Form onSubmit={handleSubmit}>
        <div className={styles.fields}>
          <TextInput
            id="name"
            name="name"
            label="Gateway name"
            placeholder="Gateway name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.name ? errors.name : undefined}
            isRequired
            isDisabled={isLoading}
            maxLength={100}
          />

          <AsyncSearchableSelect
            label="Company"
            placeholder="Select company"
            value={values.companyId}
            selectedLabel={values.companyLabel}
            onSearch={searchCompanies}
            onChange={handleCompanyChange}
            error={touched.companyId ? (errors.companyId as string | undefined) : undefined}
            isRequired
            isDisabled={isLoading}
          />

          <AsyncSearchableSelect
            label="Portfolio"
            placeholder="Select portfolio"
            value={values.portfolioId}
            selectedLabel={values.portfolioLabel}
            onSearch={searchPortfolios}
            onChange={handlePortfolioChange}
            error={touched.portfolioId ? (errors.portfolioId as string | undefined) : undefined}
            isDisabled={isLoading}
            isClearable
          />

          <div
            onMouseEnter={!isCreate ? (e) => showTooltip(e.currentTarget, LOCKED_FIELD_TOOLTIP) : undefined}
            onMouseLeave={!isCreate ? hideTooltip : undefined}
          >
            <SearchableSelect
              label="Gateway model"
              placeholder="Select gateway model"
              value={values.gatewayModel}
              onChange={handleSelectChange("gatewayModel")}
              options={GATEWAY_MODEL_OPTIONS}
              error={touched.gatewayModel ? errors.gatewayModel : undefined}
              isRequired
              isDisabled={isLoading || !isCreate}
            />
          </div>

          <div
            onMouseEnter={!isCreate ? (e) => showTooltip(e.currentTarget, LOCKED_FIELD_TOOLTIP) : undefined}
            onMouseLeave={!isCreate ? hideTooltip : undefined}
          >
          <TextInput
            id="gatewayEui"
            name="gatewayEui"
            label="Gateway EUI (Extended Unique Identifier)"
            placeholder="Gateway EUI"
            value={values.gatewayEui}
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              euiRecognitionError ?? (touched.gatewayEui ? errors.gatewayEui : undefined)
            }
            hint={
              isRecognizing
                ? "Recognizing Gateway EUI..."
                : isCreate
                  ? `${GATEWAY_EUI_MAX_LENGTH}-character hexadecimal identifier. Example: A840410000001234`
                  : undefined
            }
            isRequired
            isDisabled={isLoading || isRecognizing || !isCreate}
            maxLength={GATEWAY_EUI_MAX_LENGTH}
            afterIcon={
              isCreate ? (
                <button
                  type="button"
                  className={styles.euiButton}
                  onClick={handleUploadClick}
                  onMouseEnter={(e) => showTooltip(e.currentTarget, UPLOAD_TOOLTIP)}
                  onMouseLeave={hideTooltip}
                  onFocus={(e) => showTooltip(e.currentTarget, UPLOAD_TOOLTIP)}
                  onBlur={hideTooltip}
                  aria-label="Upload photo to recognize EUI"
                  disabled={isLoading || isRecognizing}
                >
                  <ClipboardIcon />
                </button>
              ) : undefined
            }
          />
          </div>
          {isCreate && (
            <input
              ref={fileInputRef}
              type="file"
              accept={EUI_IMAGE_ALLOWED_TYPES.join(",")}
              onChange={handleFileSelected}
              style={{ display: "none" }}
            />
          )}

          <Select
            label="Frequency band"
            placeholder="Select frequency band"
            value={values.frequencyBand}
            onChange={handleSelectChange("frequencyBand")}
            options={FREQUENCY_OPTIONS}
            error={touched.frequencyBand ? errors.frequencyBand : undefined}
            isRequired
            isDisabled
          />
        </div>

        {isCreate && (
          <div className={styles.note}>
            <p className={styles.noteTitle}>Note</p>
            <p className={styles.noteDescription}>
              After adding the gateway, you'll receive a step-by-step configuration guide to complete the physical setup
              and network integration
            </p>
            <span className={styles.noteBar} />
          </div>
        )}

        <div className={styles.buttons}>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>

          <Button type="submit" variant="primary" disabled={isLoading}>
            {isCreate ? "Add" : "Update"}
          </Button>
        </div>
      </Form>
      {tooltip &&
        createPortal(
          <div
            role="tooltip"
            className={styles.euiTooltip}
            style={{
              left: tooltip.left,
              top: tooltip.top,
              transform: tooltip.placement === "right" ? "translateY(-50%)" : "translateX(-100%)",
            }}
          >
            {tooltip.content}
          </div>,
          document.querySelector("dialog[open]") ?? document.body,
        )}
    </div>
  );
};

const defaultInitialValues: GatewayFormValues = {
  name: "",
  companyId: "",
  companyLabel: "",
  portfolioId: "",
  portfolioLabel: "",
  gatewayModel: "",
  frequencyBand: "EU868",
  gatewayEui: "",
};

const ModalCreateGatewayContent: FC<PropTypes> = ({
  onClose,
  onSubmit,
  mode = "create",
  description,
  initialData,
  isLoading,
}) => {
  const initialValues = useMemo(() => {
    if (!initialData) return defaultInitialValues;
    return { ...defaultInitialValues, ...initialData };
  }, [initialData]);

  const handleCancel = useCallback(() => {
    onClose?.();
  }, [onClose]);

  return (
    <>
      {!!description && <p className={styles.modalDescription}>{description}</p>}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSubmit?.({
            ...values,
            name: values.name.trim(),
            gatewayEui: values.gatewayEui.trim().toUpperCase(),
          });
        }}
        enableReinitialize
      >
        <FormFields onCancel={handleCancel} mode={mode} isLoading={isLoading} />
      </Formik>
    </>
  );
};

export default ModalCreateGatewayContent;
